import { getPromotionStatus, PromotionStatus } from '@/utilities/api/PromotionUtilities';
import { escapeCsvFormula } from '@/utilities/Export';
import { MetricsEndpoint } from '@api/endpoints';
import { stringify } from 'csv-stringify/browser/esm/sync';
import { TFunction } from 'i18next';
import { round } from 'lodash';
import { countPromotionStatuses, metricColumns, promotionLabel } from './metricColumns';
import { periodLabel } from './periodLabel';

type FundingMetrics = MetricsEndpoint.GetPointsMetrics.FundingMetrics;
type Metrics = MetricsEndpoint.GetPointsMetrics.PointsMetrics;
type Row = MetricsEndpoint.GetPointsMetrics.PointsMetricsRow;

const metricHeaders = (t: TFunction) => metricColumns.map(({ column }) => (
  column === 'redemptionRate'
    ? t($ => $.pointsMetrics.export.redemptionRatePercent)
    : t($ => $.pointsMetrics.columns[column])
));

const metricValues = (metrics: Metrics) => metricColumns.map(({ key }) => (
  key === 'pointsRedemptionRate' ? round(metrics[key] * 100, 2) : metrics[key]
));

const toCsv = (records: unknown[][]) => stringify(records.map((record) => (
  record.map(escapeCsvFormula)
)));

export const buildFundingCsv = (funding: FundingMetrics, t: TFunction): string => toCsv([
  [t($ => $.pointsMetrics.columns.funding), ...metricHeaders(t)],
  [t($ => $.pointsMetrics.funding.retailer), ...metricValues(funding.retailer)],
  [t($ => $.pointsMetrics.funding.manufacturer), ...metricValues(funding.manufacturer)],
  [t($ => $.pointsMetrics.funding.total), ...metricValues(funding.total)],
]);

/** Every row, expanded or not: each period's total, funding split, manufacturers, promotions and wallets. */
export const buildBreakdownCsv = (rows: Row[], t: TFunction): string => {
  const header = [
    t($ => $.pointsMetrics.columns.retailer),
    t($ => $.pointsMetrics.columns.period),
    t($ => $.pointsMetrics.export.level),
    t($ => $.pointsMetrics.export.name),
    ...metricHeaders(t),
    t($ => $.pointsMetrics.export.promotionStatus),
    t($ => $.pointsMetrics.export.pointsAwarded),
    t($ => $.pointsMetrics.export.promotionsRunning),
    t($ => $.pointsMetrics.export.promotionsFuture),
    t($ => $.pointsMetrics.export.promotionsExpired),
  ];
  const records = rows.flatMap((row) => {
    const period = periodLabel(row.period.type === 'outstanding' ? null : row.period, t);
    const line = (level: string, name: string, metrics: Metrics, extra: unknown[] = []) => {
      const values = [row.retailerName, period, level, name, ...metricValues(metrics), ...extra];
      return [...values, ...Array(header.length - values.length).fill('')];
    };
    return [
      line(t($ => $.pointsMetrics.funding.total), '', row.metrics.total),
      line(t($ => $.pointsMetrics.funding.retailer), '', row.metrics.retailer),
      line(t($ => $.pointsMetrics.funding.manufacturer), '', row.metrics.manufacturer),
      ...row.manufacturers.map((manufacturer) => line(
        t($ => $.pointsMetrics.export.levels.manufacturer),
        manufacturer.manufacturerName ?? t($ => $.pointsMetrics.unknownManufacturer),
        manufacturer,
      )),
      ...row.promotions.map((promotion) => line(
        t($ => $.pointsMetrics.export.levels.promotion),
        promotionLabel(promotion, t),
        promotion,
        [
          promotion.startDate && promotion.endDate
            ? t($ => $.promotions.status[getPromotionStatus({
              endDate: promotion.endDate as string,
              startDate: promotion.startDate as string,
            })])
            : '',
          promotion.pointsAwarded ?? '',
        ],
      )),
      ...row.wallets.map((wallet) => {
        const counts = countPromotionStatuses(wallet.promotionPeriods);
        return line(
          t($ => $.pointsMetrics.export.levels.wallet),
          wallet.subwalletName ?? t($ => $.pointsMetrics.wallets.unknown),
          wallet,
          [
            '',
            '',
            counts[PromotionStatus.RUNNING],
            counts[PromotionStatus.FUTURE],
            counts[PromotionStatus.EXPIRED],
          ],
        );
      }),
    ];
  });
  return toCsv([header, ...records]);
};
